import { useQuery } from "@tanstack/react-query";
import { syncLabelsApi, type SyncLabelFilters } from "@/api";
import { MultiSelectFilter } from "./MultiSelectFilter";
import { Badge } from "@/components/ui/badge";

interface SyncLabelFilterProps {
  buyerStage: Set<string>;
  onBuyerStageChange: (v: Set<string>) => void;
  serpType: Set<string>;
  onSerpTypeChange: (v: Set<string>) => void;
  group: Set<string>;
  onGroupChange: (v: Set<string>) => void;
  tag: Set<string>;
  onTagChange: (v: Set<string>) => void;
  projectId?: string;
}

export function SyncLabelFilter({
  buyerStage, onBuyerStageChange,
  serpType, onSerpTypeChange,
  group, onGroupChange,
  tag, onTagChange,
  projectId,
}: SyncLabelFilterProps) {
  const { data: filters } = useQuery<SyncLabelFilters>({
    queryKey: ["syncLabelFilters", projectId],
    queryFn: () => syncLabelsApi.getFilters(projectId),
    staleTime: 60_000,
  });

  if (!filters) return null;

  const hasOptions = filters.buyer_stage.length > 0 ||
    filters.serp_type.length > 0 ||
    filters.groups.length > 0 ||
    filters.tags.length > 0;

  if (!hasOptions) return null;

  const toOptions = (arr: string[]) =>
    arr.map(v => ({ value: v, label: v }));

  return (
    <div className="flex gap-2 flex-wrap items-center">
      <span className="text-xs text-muted-foreground font-medium">標籤篩選:</span>
      {filters.buyer_stage.length > 0 && (
        <MultiSelectFilter
          label="消費歷程"
          options={toOptions(filters.buyer_stage)}
          selected={buyerStage}
          onChange={onBuyerStageChange}
        />
      )}
      {filters.serp_type.length > 0 && (
        <MultiSelectFilter
          label="SERP 類型"
          options={toOptions(filters.serp_type)}
          selected={serpType}
          onChange={onSerpTypeChange}
        />
      )}
      {filters.groups.length > 0 && (
        <MultiSelectFilter
          label="群組"
          options={toOptions(filters.groups)}
          selected={group}
          onChange={onGroupChange}
        />
      )}
      {filters.tags.length > 0 && (
        <MultiSelectFilter
          label="標籤"
          options={toOptions(filters.tags)}
          selected={tag}
          onChange={onTagChange}
        />
      )}
    </div>
  );
}

// Badge display for task rows
interface SyncLabelBadgesProps {
  labels?: Record<string, unknown> | null;
  groupPath?: string | null;
  tags?: string[] | null;
}

export function SyncLabelBadges({ labels, groupPath, tags }: SyncLabelBadgesProps) {
  if (!labels && !groupPath && !tags?.length) return null;

  return (
    <div className="flex gap-1 flex-wrap">
      {labels?.buyer_stage && (
        <Badge variant="outline" className="text-[10px] border-blue-500 text-blue-400">
          {String(labels.buyer_stage)}
        </Badge>
      )}
      {labels?.serp_type && (
        <Badge variant="outline" className="text-[10px] border-yellow-500 text-yellow-400">
          {String(labels.serp_type)}
        </Badge>
      )}
      {groupPath && (
        <Badge variant="outline" className="text-[10px] border-gray-500 text-gray-400">
          {groupPath}
        </Badge>
      )}
      {tags?.map(t => (
        <Badge key={t} variant="outline" className="text-[10px] border-purple-500 text-purple-400">
          #{t}
        </Badge>
      ))}
    </div>
  );
}
